import React, { useEffect, useState } from "react";
import { Fuel, Plus, ChevronDown, ChevronUp, Check, X, Truck, Ban, CheckCircle2 } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";
import { fmtDate } from "../lib/format.js";

// Colours per status
const STATUS_STYLE = {
  Pendente: "bg-[#FFF3CD] text-[#856404] border-[#FFC107]/40",
  Aprovado: "bg-[#CFE2FF] text-[#084298] border-[#0D6EFD]/40",
  Rejeitado: "bg-[#F8D7DA] text-[#721C24] border-[#DC3545]/40",
  "Combustível retirado": "bg-[#FFE5B4] text-[#7A4E00] border-[#FD7E14]/40",
  Consumido: "bg-[#D4EDDA] text-[#155724] border-[#28A745]/40",
  Cancelado: "bg-gray-100 text-gray-600 border-gray-300",
};

const CREATE_ROLES = ["admin", "national_aftersales_manager", "department_manager", "workshop_supervisor", "workshop_coordinator"];
const APPROVE_ROLES = ["admin", "national_aftersales_manager", "department_manager"];
const COLLECT_ROLES = ["admin", "workshop_supervisor", "workshop_coordinator"];
const CONSUME_ROLES = ["admin", "workshop_supervisor", "workshop_coordinator"];

export default function FuelRequestsSection({ caseId, caseNumber }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/service-cases/${caseId}/fuel-requests`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [caseId]);

  const canCreate = CREATE_ROLES.includes(user?.role);

  return (
    <div className="bg-white border border-gray-200 rounded-md p-4 mb-4" data-testid="fuel-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-bold text-[#1A1A2E] uppercase tracking-wider flex items-center gap-2">
          <Fuel className="w-4 h-4 text-[#1F3864]" />
          Pedidos de Combustível
          <span className="text-xs font-mono bg-[#F4F6F8] text-[#6C757D] px-1.5 py-0.5 rounded-sm" data-testid="fuel-count">
            {items.length}
          </span>
        </h3>
        {canCreate && (
          <button
            onClick={() => setShowNew(true)}
            className="text-xs border border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864] hover:text-white rounded-sm px-2.5 py-1 flex items-center gap-1"
            data-testid="fuel-new-btn"
          >
            <Plus className="w-3 h-3" /> Novo pedido
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-[#6C757D]">A carregar…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-[#6C757D]" data-testid="fuel-empty">Ainda não existem pedidos de combustível para esta obra.</div>
      ) : (
        <div className="border border-gray-100 rounded-sm overflow-hidden">
          <div className="grid grid-cols-[100px_100px_90px_90px_140px_1fr_120px_28px] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#6C757D] bg-[#F4F6F8]">
            <div>FUEL</div><div>Combustível</div><div>Qt. ped.</div><div>Qt. apr.</div><div>Estado</div><div>Pedido por</div><div>Criado</div><div></div>
          </div>
          {items.map((f) => (
            <FuelRow
              key={f.id}
              fuel={f}
              expanded={expandedId === f.id}
              onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)}
              onChange={load}
              user={user}
            />
          ))}
        </div>
      )}

      {showNew && (
        <NewFuelDialog
          caseId={caseId}
          caseNumber={caseNumber}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}

function FuelRow({ fuel, expanded, onToggle, onChange, user }) {
  return (
    <div className="border-t border-gray-100 first:border-0" data-testid={`fuel-row-${fuel.id}`}>
      <button
        onClick={onToggle}
        className="w-full text-left grid grid-cols-[100px_100px_90px_90px_140px_1fr_120px_28px] gap-2 px-3 py-2 text-sm hover:bg-[#F4F6F8] items-center"
      >
        <div className="font-mono text-[#1F3864] font-semibold">{fuel.fuel_request_number}</div>
        <div>{fuel.fuel_type}</div>
        <div className="font-mono">{fuel.quantity_requested_litres}L</div>
        <div className="font-mono text-[#6C757D]">{fuel.quantity_approved_litres != null ? `${fuel.quantity_approved_litres}L` : "—"}</div>
        <div>
          <span className={`inline-flex text-[10px] font-semibold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${STATUS_STYLE[fuel.status] || STATUS_STYLE.Pendente}`}>
            {fuel.status}
          </span>
        </div>
        <div className="truncate text-[#6C757D]">{fuel.requested_by_name || "—"}</div>
        <div className="text-[#6C757D] text-xs">{fmtDate(fuel.created_at)}</div>
        <div className="text-[#6C757D]">{expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</div>
      </button>
      {expanded && <FuelDetails fuel={fuel} onChange={onChange} user={user} />}
    </div>
  );
}

function FuelDetails({ fuel, onChange, user }) {
  const role = user?.role;
  const canApprove = APPROVE_ROLES.includes(role) && fuel.status === "Pendente";
  const canCollect = COLLECT_ROLES.includes(role) && fuel.status === "Aprovado";
  const canConsume = CONSUME_ROLES.includes(role) && (fuel.status === "Combustível retirado" || fuel.status === "Aprovado");
  const isRequester = fuel.requested_by_id === user?.id;
  const isManager = ["admin", "national_aftersales_manager", "department_manager"].includes(role);
  const canCancel = (fuel.status === "Pendente" && isRequester) || (isManager && (fuel.status === "Pendente" || fuel.status === "Aprovado"));

  return (
    <div className="px-4 py-3 bg-[#FAFBFC] border-t border-gray-100">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
        <Cell label="Justificação" wide>{fuel.purpose}</Cell>
        <Cell label="Pedido">{fmtDate(fuel.requested_at)} · {fuel.requested_by_name || "—"}</Cell>
        {fuel.approved_at && <Cell label="Aprovado">{fmtDate(fuel.approved_at)} · {fuel.approved_by_name || "—"}</Cell>}
        {fuel.approval_notes && <Cell label="Notas de aprovação">{fuel.approval_notes}</Cell>}
        {fuel.rejected_at && <Cell label="Rejeitado">{fmtDate(fuel.rejected_at)} · {fuel.rejected_by_name || "—"}</Cell>}
        {fuel.rejection_reason && <Cell label="Motivo da rejeição" wide><span className="text-[#721C24]">{fuel.rejection_reason}</span></Cell>}
        {fuel.supplier_requisition_number && <Cell label="Requisição fornecedor" mono>{fuel.supplier_requisition_number}</Cell>}
        {fuel.collected_at && <Cell label="Retirado">{fmtDate(fuel.collected_at)} · {fuel.collected_by_name || "—"}</Cell>}
        {fuel.quantity_used_litres != null && <Cell label="Consumido">{fuel.quantity_used_litres}L</Cell>}
        {fuel.used_at && <Cell label="Data consumo">{fmtDate(fuel.used_at)} · {fuel.used_confirmed_by_name || "—"}</Cell>}
        {fuel.cancelled_at && <Cell label="Cancelado">{fmtDate(fuel.cancelled_at)}</Cell>}
        {fuel.cancellation_reason && <Cell label="Motivo do cancelamento" wide>{fuel.cancellation_reason}</Cell>}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {canApprove && <ApproveActionForm fuel={fuel} onChange={onChange} />}
        {canApprove && <RejectActionForm fuel={fuel} onChange={onChange} />}
        {canCollect && <CollectActionForm fuel={fuel} onChange={onChange} />}
        {canConsume && <ConsumeActionForm fuel={fuel} onChange={onChange} />}
        {canCancel && <CancelActionForm fuel={fuel} onChange={onChange} />}
      </div>
    </div>
  );
}

function Cell({ label, children, mono, wide }) {
  return (
    <div className={wide ? "md:col-span-3" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className={`text-sm text-[#1A1A2E] ${mono ? "font-mono" : ""}`}>{children}</div>
    </div>
  );
}

function InlineForm({ label, testid, icon: Icon, colour, children, onSubmit, submitLabel }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  return (
    <div className="w-full">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid={`fuel-btn-${testid}`}
          className={`text-xs px-3 py-1.5 rounded-sm border flex items-center gap-1 ${colour}`}
        >
          <Icon className="w-3.5 h-3.5" /> {label}
        </button>
      ) : (
        <div className="w-full border border-gray-200 bg-white rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-2">{label}</div>
          {children}
          {err && <div className="mt-2 text-xs text-[#721C24]" data-testid={`fuel-error-${testid}`}>{err}</div>}
          <div className="mt-2 flex gap-2 justify-end">
            <button onClick={() => { setOpen(false); setErr(""); }} className="text-xs px-3 py-1.5 border border-gray-300 rounded-sm">Cancelar</button>
            <button
              onClick={async () => {
                setBusy(true); setErr("");
                try { await onSubmit(); setOpen(false); }
                catch (e) { setErr(formatError(e.response?.data?.detail)); }
                finally { setBusy(false); }
              }}
              disabled={busy}
              data-testid={`fuel-submit-${testid}`}
              className="text-xs px-3 py-1.5 bg-[#1F3864] text-white rounded-sm disabled:opacity-60"
            >
              {busy ? "…" : submitLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full px-2 py-1 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";

function ApproveActionForm({ fuel, onChange }) {
  const [qty, setQty] = useState(fuel.quantity_requested_litres);
  const [notes, setNotes] = useState("");
  return (
    <InlineForm
      label="Aprovar"
      testid="approve"
      icon={Check}
      colour="border-[#28A745] text-[#155724] bg-[#D4EDDA]/40 hover:bg-[#D4EDDA]"
      submitLabel="Aprovar"
      onSubmit={async () => {
        await api.post(`/fuel-requests/${fuel.id}/approve`, { quantity_approved_litres: parseFloat(qty), approval_notes: notes || null });
        onChange();
      }}
    >
      <label className="block mb-2">
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Quantidade aprovada (L)</span>
        <input type="number" step="0.1" value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} data-testid="fuel-approve-qty" />
      </label>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Notas (opcional)</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className={`${inputCls} h-16`} />
      </label>
    </InlineForm>
  );
}

function RejectActionForm({ fuel, onChange }) {
  const [reason, setReason] = useState("");
  return (
    <InlineForm
      label="Rejeitar"
      testid="reject"
      icon={X}
      colour="border-[#DC3545] text-[#721C24] bg-[#F8D7DA]/40 hover:bg-[#F8D7DA]"
      submitLabel="Rejeitar"
      onSubmit={async () => {
        await api.post(`/fuel-requests/${fuel.id}/reject`, { rejection_reason: reason });
        onChange();
      }}
    >
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Motivo da rejeição *</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} className={`${inputCls} h-16`} data-testid="fuel-reject-reason" />
      </label>
    </InlineForm>
  );
}

function CollectActionForm({ fuel, onChange }) {
  const [req, setReq] = useState("");
  return (
    <InlineForm
      label="Registar recolha"
      testid="collect"
      icon={Truck}
      colour="border-[#FD7E14] text-[#7A4E00] bg-[#FFE5B4]/40 hover:bg-[#FFE5B4]"
      submitLabel="Marcar retirado"
      onSubmit={async () => {
        await api.post(`/fuel-requests/${fuel.id}/collect`, { supplier_requisition_number: req });
        onChange();
      }}
    >
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Nº requisição fornecedor *</span>
        <input value={req} onChange={(e) => setReq(e.target.value)} className={inputCls + " font-mono"} data-testid="fuel-collect-req" />
      </label>
    </InlineForm>
  );
}

function ConsumeActionForm({ fuel, onChange }) {
  const [qty, setQty] = useState(fuel.quantity_approved_litres || fuel.quantity_requested_litres);
  return (
    <InlineForm
      label="Confirmar consumo"
      testid="consume"
      icon={CheckCircle2}
      colour="border-[#28A745] text-[#0F5132] bg-[#D1F2E1]/40 hover:bg-[#D1F2E1]"
      submitLabel="Confirmar consumo"
      onSubmit={async () => {
        await api.post(`/fuel-requests/${fuel.id}/consume`, { quantity_used_litres: parseFloat(qty) });
        onChange();
      }}
    >
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Quantidade efectivamente usada (L)</span>
        <input type="number" step="0.1" value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} data-testid="fuel-consume-qty" />
      </label>
    </InlineForm>
  );
}

function CancelActionForm({ fuel, onChange }) {
  const [reason, setReason] = useState("");
  return (
    <InlineForm
      label="Cancelar"
      testid="cancel"
      icon={Ban}
      colour="border-gray-400 text-gray-700 bg-white hover:bg-gray-100"
      submitLabel="Cancelar pedido"
      onSubmit={async () => {
        await api.post(`/fuel-requests/${fuel.id}/cancel`, { reason });
        onChange();
      }}
    >
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Motivo do cancelamento *</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} className={`${inputCls} h-16`} data-testid="fuel-cancel-reason" />
      </label>
    </InlineForm>
  );
}

function NewFuelDialog({ caseId, caseNumber, onClose, onCreated }) {
  const [form, setForm] = useState({ fuel_type: "Diesel", quantity_requested_litres: 5, purpose: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/fuel-requests", {
        service_case_id: caseId,
        fuel_type: form.fuel_type,
        quantity_requested_litres: parseFloat(form.quantity_requested_litres),
        purpose: form.purpose,
      });
      onCreated();
    } catch (e) {
      setErr(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="new-fuel-dialog">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2">
            <Fuel className="w-4 h-4 text-[#1F3864]" />
            Novo pedido de combustível
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-[#F4F6F8] rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-[#6C757D]">Obra: <span className="font-mono">{caseNumber}</span></div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Tipo de combustível *</span>
            <select value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })} className={inputCls} data-testid="new-fuel-type">
              <option>Diesel</option><option>Gasolina</option><option>Gasóleo</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Quantidade (L) *</span>
            <input type="number" step="0.1" value={form.quantity_requested_litres} onChange={(e) => setForm({ ...form, quantity_requested_litres: e.target.value })} className={inputCls} data-testid="new-fuel-qty" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Justificação *</span>
            <textarea value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className={`${inputCls} h-24`} placeholder="Ex.: Teste de estrada após substituição de injectores" data-testid="new-fuel-purpose" />
          </label>
          {err && <div className="text-xs text-[#721C24] bg-[#F8D7DA] border border-[#DC3545]/30 rounded-sm px-2 py-1.5" data-testid="new-fuel-error">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm">Cancelar</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 text-sm bg-[#1F3864] text-white rounded-sm disabled:opacity-60" data-testid="new-fuel-submit">
            {busy ? "A submeter…" : "Submeter pedido"}
          </button>
        </div>
      </div>
    </div>
  );
}
